import { useCallback } from 'react';
import { findClientIdByCampaignId, validateDateRange, createBatchJobOptions } from '../utils/campaignHelpers';

/**
 * Hook for sending batch jobs for campaigns
 * @param {Object} params
 * @param {Object} params.wsProvider - WebSocket provider
 * @param {Object} params.clientCampaigns - Campaigns data
 * @param {Object} params.tableValues - Table values with actions and dates
 * @param {string} params.actionKey - Key for action in tableValues (e.g., 'sendInvoicesAction', 'sendReportsAction')
 * @param {string} params.fromDateKey - Key for from date (e.g., 'sendInvoicesFromDate')
 * @param {string} params.toDateKey - Key for to date (e.g., 'sendInvoicesToDate')
 * @param {string} params.actionValue - Value to match for selected campaigns (e.g., 'send_report_and_invoice', 'send_report')
 * @param {boolean} params.createInvoice - Whether to create invoice
 * @param {boolean} params.attachInvoice - Whether to attach invoice
 * @param {boolean} params.updateReportHistory - Whether to update report history
 * @param {Function} params.triggerTableRerender - Function to trigger table rerender
 * @param {string} params.emptyMessage - Message when no campaigns selected
 * @returns {Function} Function to send batch jobs
 */
export const useBatchJobSender = ({
    wsProvider,
    clientCampaigns,
    tableValues,
    actionKey,
    fromDateKey,
    toDateKey,
    actionValue,
    createInvoice = false,
    attachInvoice = false,
    updateReportHistory = false,
    triggerTableRerender,
    emptyMessage = 'Please select at least one campaign.'
}) => {
    const sendBatchJobs = useCallback(async () => {
        const selectedCampaigns = [];
        
        if (!tableValues[actionKey]) {
            alert(emptyMessage);
            return;
        }

        // Collect selected campaigns with validation
        Object.entries(tableValues[actionKey]).forEach(([campaignId, action]) => {
            if (action === actionValue) {
                const fromDate = tableValues[fromDateKey]?.[campaignId];
                const toDate = tableValues[toDateKey]?.[campaignId];
                
                // Validate dates
                const validation = validateDateRange(fromDate, toDate, campaignId);
                if (!validation.valid) {
                    alert(validation.error);
                    return;
                }
                
                // Find client ID
                const clientId = findClientIdByCampaignId(clientCampaigns, campaignId);
                if (!clientId) {
                    alert(`Could not find client for campaign ${campaignId}`);
                    return;
                }
                
                selectedCampaigns.push({
                    campaignId,
                    fromDate,
                    toDate,
                    clientId
                });
            }
        });

        if (selectedCampaigns.length === 0) {
            alert(emptyMessage);
            return;
        }

        alert(`Initiating batch jobs for ${selectedCampaigns.length} selected campaign(s).`);

        // Trigger batch job for each selected campaign with 5-second delays
        for (const job_input_data of selectedCampaigns) {
            const batchJobOptions = createBatchJobOptions({
                clientId: job_input_data.clientId,
                campaignId: job_input_data.campaignId,
                fromDate: job_input_data.fromDate,
                toDate: job_input_data.toDate,
                projectId: wsProvider.selectedProjectId,
                createInvoice,
                attachInvoice,
                updateReportHistory
            });

            console.log(batchJobOptions);

            wsProvider.ws.waitForSocketAndSend({
                action: "batchJobInit",
                options: {
                    batchJobName: "edufind.CampaignStats",
                    batchJobOptions: batchJobOptions,
                    project_id: wsProvider.selectedProjectId
                }
            }, (response) => {
                console.log(`Batch job initiated for campaign ${job_input_data.campaignId}:`, response);
            });

            // Wait 5 seconds before the next job (except for the last one)
            if (job_input_data !== selectedCampaigns[selectedCampaigns.length - 1]) {
                await new Promise(resolve => setTimeout(resolve, 5000));
            }
        }

        // Show completion alert after all jobs are initiated
        alert(`All ${selectedCampaigns.length} batch jobs have been initiated with 5-second intervals.`);
        
        // Trigger table rerender to reflect any changes
        if (triggerTableRerender) {
            triggerTableRerender();
        }
    }, [
        wsProvider,
        clientCampaigns,
        tableValues,
        actionKey,
        fromDateKey,
        toDateKey,
        actionValue,
        createInvoice,
        attachInvoice,
        updateReportHistory,
        triggerTableRerender,
        emptyMessage
    ]);

    return sendBatchJobs;
};

